/**
 * BulkRequestModal — post one "request research help" mystery for a whole
 * bucket of persons at once (Mysteries Layer 3 #3). Driven from DirectoryView
 * gap filters: e.g. "everyone missing parents" → one investigate_gap quest per
 * person, in a single click. Submits to POST /api/mysteries/requests/bulk.
 *
 * Owner-only (the server re-checks ownership) and self-solve-safe (the quests
 * are origin='user' with no from_mystery_id). The server skips persons that
 * already carry an open owner-authored gap, so repeat clicks don't duplicate.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../services/api.js'

const GAP_TYPES = [
  { value: 'vertical',   label: 'Vertical' },
  { value: 'horizontal', label: 'Horizontal' },
  { value: 'whitespot',  label: 'Whitespot' },
]

export default function BulkRequestModal({
  treeId, personIds, count, defaultText = '', defaultGapType = 'vertical', onClose,
}) {
  const navigate = useNavigate()
  const [description, setDescription] = useState(defaultText)
  const [gapType, setGapType] = useState(defaultGapType)
  const [posting, setPosting] = useState(false)
  const [err, setErr] = useState(null)
  const [result, setResult] = useState(null) // { created, skipped }

  async function submit(e) {
    e.preventDefault()
    if (!description.trim()) { setErr('A description is required.'); return }
    setPosting(true); setErr(null)
    try {
      const r = await api.post('/mysteries/requests/bulk', {
        tree_id: treeId,
        person_ids: personIds,
        description: description.trim(),
        gap_type: gapType,
      })
      setResult(r)
    } catch (e2) {
      setErr(e2.message || 'Could not create the requests')
      setPosting(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal request-research-modal" onClick={e => e.stopPropagation()}>
        <header className="modal__header">
          <h2>{result ? 'Requests created' : `Request research help on ${count}`}</h2>
          <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </header>

        {result ? (
          <div className="request-research-modal__body">
            <p className="request-research-modal__intro">
              Created <strong>{result.created}</strong> research request{result.created === 1 ? '' : 's'}.
              {result.skipped > 0 && ` ${result.skipped} skipped (already had an open request or aren’t eligible).`}
              {' '}They’re in your Suggestions Inbox — Promote them or attach bounties from there.
            </p>
            <div className="request-research-modal__actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Close</button>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/suggestions')}>
                Go to inbox →
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="request-research-modal__body">
            <p className="request-research-modal__intro">
              This posts one mystery for each of the <strong>{count}</strong> people in this
              filter. The same description applies to all; each is titled with the person’s name.
              People who already have an open request are skipped.
            </p>

            <label className="request-research-modal__field">
              <span>What do you need on each?</span>
              <textarea
                value={description} rows={3} maxLength={2000}
                onChange={e => setDescription(e.target.value)}
                placeholder="e.g. Help me find the parents of this person."
              />
            </label>

            <div className="request-research-modal__field">
              <span>Type of gap</span>
              <div className="request-research-modal__gaptypes">
                {GAP_TYPES.map(g => (
                  <button
                    key={g.value} type="button"
                    className={`request-research-modal__gaptype ${gapType === g.value ? 'is-active' : ''}`}
                    onClick={() => setGapType(g.value)}
                  >{g.label}</button>
                ))}
              </div>
            </div>

            {err && <div className="request-research-modal__err">{err}</div>}

            <div className="request-research-modal__actions">
              <button type="button" className="btn btn-ghost" onClick={onClose} disabled={posting}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={posting}>
                {posting ? 'Creating…' : `Create ${count} request${count === 1 ? '' : 's'} →`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
